import { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Laptop, X } from 'lucide-react';
import { usePhoneWidth } from '@/hooks';

const DISMISSED_KEY = 'phone-notice-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * On a phone, most of the app is cramped. Work Orders is built to fit, so the notice points
 * there. It isn't shown on the pages built to fit a phone. Once dismissed it stays dismissed on this device.
 */
const FITS_A_PHONE = ['/app/work-orders', '/app/vote-goal'];

export function PhoneNotice() {
  const phone = usePhoneWidth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const fits = FITS_A_PHONE.some((page) => pathname === page || pathname.startsWith(`${page}/`));

  if (!phone || dismissed || fits) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Without storage it stays dismissed until the next reload.
    }
  };

  return (
    <section
      aria-label="Works best on a computer"
      className="flex items-center gap-2 border-b bg-muted px-4 py-2 text-sm text-muted-foreground"
    >
      <Laptop className="size-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        This works best on a computer.{' '}
        <Link to="/app/work-orders" className="font-medium text-primary underline underline-offset-2">
          Go to Work Orders
        </Link>
      </p>
      <button type="button" aria-label="Dismiss" onClick={dismiss} className="-mr-1 rounded p-1 hover:bg-background">
        <X className="size-4" aria-hidden="true" />
      </button>
    </section>
  );
}
